import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom-v5-compat";
import { SupportHubSource } from "@b2bportal/core-self-serve";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import { ProductType } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import { getDelegatedTo } from "@hopper-b2b/self-serve";
import { ResendEmailModal } from "@tenant/self-serve";
import { ContactSupportModal, Icon } from "@tenant/ui";
import clsx from "clsx";
import styles from "./ManageBooking.module.scss";
import type { ManageBookingProps } from "./types";
import { useManageBookingContext } from "./useManageBookingContext";
import { DISRUPTION_EXERCISE_PATH_WITH_ITINERARY_ID } from "@/utilities/constants";

export const ManageBooking = (props: ManageBookingProps) => {
  const { onQueryCancellation, onQueryExchange } = props;
  const onCfarExercise =
    props.variant === ProductType.LODGING ||
    props.variant === ProductType.FLIGHTS
      ? props.onCfarExercise
      : undefined;
  const { className } = props;
  const { t } = useI18nContext();
  const navigate = useNavigate();
  const [block, cn] = useModuleBEM(styles, "ManageBooking");
  const {
    reservationId,
    bookingType,
    showDisruptionExercise,

    // Cancellation
    showCancel,
    cancelLabel,

    // Exchange
    exchangeLabel,
    showExchange,

    // CFAR
    showCfarExercise,
  } = useManageBookingContext(props);

  const itineraryId =
    props.variant === ProductType.FLIGHTS
      ? props.itinerary.bookedItinerary?.id
      : undefined;
  const isAgentImpersonation = !!useSelector(getDelegatedTo);

  return (
    <div className={clsx(block, className)}>
      <h3 className={cn("title")}>{t("manageBooking.title")}</h3>
      <div className={cn("actions")}>
        <ResendEmailModal
          title={
            <span className={cn("action-content")}>
              <Icon iconName={IconNameEnum.email} />
              {t("trips.tripReviewLinks.resendEmail")}
            </span>
          }
          reservationId={reservationId}
          reservationType={bookingType}
          disabled={isAgentImpersonation}
        />
        {showCancel && onQueryCancellation && (
          <button
            className={cn("action-button")}
            onClick={onQueryCancellation}
            type="button"
          >
            <Icon iconName={IconNameEnum.cancelCircle} />
            {cancelLabel}
          </button>
        )}
        {showCfarExercise && onCfarExercise && (
          <button
            className={cn("action-button")}
            onClick={onCfarExercise}
            type="button"
          >
            <Icon iconName={IconNameEnum.cancelCircle} />
            {t("selfServe.cancelForAnyReason")}
          </button>
        )}
        {showExchange && onQueryExchange ? (
          <button
            className={cn("action-button")}
            onClick={onQueryExchange}
            type="button"
          >
            <Icon iconName={IconNameEnum.exchange} />
            {exchangeLabel}
          </button>
        ) : showExchange ? (
          <ContactSupportModal
            className={cn("action-button")}
            trigger={
              <>
                <Icon iconName={IconNameEnum.exchange} />
                {exchangeLabel}
              </>
            }
          />
        ) : null}
        <ContactSupportModal
          enableHtsAssistChoice
          source={SupportHubSource.manage_booking}
          trigger={
            <>
              <Icon iconName={IconNameEnum.questionCircle} />
              {t("trips.tripReviewLinks.contactSupport")}
            </>
          }
        />
        {showDisruptionExercise &&
          props.variant === ProductType.FLIGHTS &&
          itineraryId && (
            <button
              className={cn("action-button", ["single-line"])}
              onClick={() =>
                navigate(
                  DISRUPTION_EXERCISE_PATH_WITH_ITINERARY_ID(itineraryId)
                )
              }
              type="button"
            >
              <Icon iconName={IconNameEnum.premiumDisruptionAssistance} />
              {t("core-disruption.productName")}
            </button>
          )}
      </div>
    </div>
  );
};
